import type { MessageFieldsFragment } from '#src/graphql/generated';

import {
  Badge,
  Box,
  Button,
  DialogActionTrigger,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  IconButton,
  Spinner,
  Switch,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import MDEditor from '@uiw/react-md-editor';
import { useState } from 'react';
import {
  FaCopy,
  FaExpand,
  FaFont,
  FaMicrophone,
  FaRobot,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

import { CenteredDialogContent } from '#src/components/ui/centered-dialog-content';
import { useColorMode } from '#src/components/ui/color-mode';
import { summarizeMessageForThreadPreview } from '#src/utils/message-thread-focus';

/** Options for executeProcessor when running the chat-message-query processor. */
export type ChatMessageQueryOptions = {
  applyDocumentEdits?: boolean;
};

function getSourceIcon(source: string) {
  switch (source) {
    case 'text':
      return FaFont;
    case 'audio':
      return FaMicrophone;
    case 'ai-note':
      return FaRobot;
    default:
      return null;
  }
}

export type MessageCardVariant = 'collapsed' | 'expanded';

interface MessageCardProps {
  message: MessageFieldsFragment;
  childMessages?: MessageFieldsFragment[];
  variant: MessageCardVariant;
  /** Collapsed: expand thread in URL. Omitted for summary rows. */
  onExpandThread?: (rootId: string) => void;
  onRunChatMessageQuery: (
    messageId: string,
    query: string,
    options?: ChatMessageQueryOptions,
  ) => Promise<void>;
  chatMessageQueryLoadingId: string | null;
}

export function MessageCard({
  message,
  childMessages,
  variant,
  onExpandThread,
  onRunChatMessageQuery,
  chatMessageQueryLoadingId,
}: MessageCardProps) {
  const { colorMode } = useColorMode();
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteQuery, setNoteQuery] = useState('');
  const [applyDocumentEdits, setApplyDocumentEdits] = useState(false);

  const documentContext = message.messageContexts?.find(
    (ctx) => ctx.name === 'documentId' && ctx.document,
  );
  const document = documentContext?.document;

  const isSystemMessage = message.userId === 'system';
  const isNoteLoading = chatMessageQueryLoadingId === message.id;
  const isRoot = message.parentMessageId == null;
  const showExpand =
    variant === 'collapsed' && isRoot && Boolean(onExpandThread);

  const handleCloseNoteDialog = () => {
    setNoteDialogOpen(false);
    setNoteQuery('');
    setApplyDocumentEdits(false);
  };

  const handleSubmitNote = async () => {
    try {
      await onRunChatMessageQuery(message.id, noteQuery, {
        applyDocumentEdits: document ? applyDocumentEdits : false,
      });
      handleCloseNoteDialog();
    } catch {
      /* Error toast shown by parent */
    }
  };

  return (
    <Box>
      <Box
        p={4}
        backgroundColor="var(--background-base-highlight-color)"
        borderRadius="md"
        borderColor="var(--border-base-color)"
        borderWidth="var(--border-base-width)"
      >
        <HStack justify="space-between" align="start" mb={2}>
          <HStack gap={2} align="center" flexWrap="wrap">
            <Text
              fontSize="xs"
              fontWeight="medium"
              color={isSystemMessage ? 'var(--brand-base-color)' : 'fg'}
            >
              {message.author?.displayName ?? 'Unknown'}
            </Text>
            <Text fontSize="xs" color="fg.muted">
              ·
            </Text>
            <Text fontSize="xs" color="fg.muted">
              {new Date(message.createdAt).toLocaleString()}
            </Text>
            {getSourceIcon(message.source) && (
              <Box
                as={getSourceIcon(message.source)!}
                aria-label={message.source}
                color={
                  message.source === 'ai-note'
                    ? 'var(--brand-base-color)'
                    : 'fg.subtle'
                }
                fontSize="xs"
                display="inline-flex"
              />
            )}
            {document && (
              <Link
                to={`/documents/${document.path}`}
                style={{ textDecoration: 'none' }}
              >
                <Badge
                  as="span"
                  colorPalette="blue"
                  variant="subtle"
                  size="sm"
                  cursor="pointer"
                  _hover={{ opacity: 0.9 }}
                >
                  View Document ➡️
                </Badge>
              </Link>
            )}
          </HStack>
          <HStack gap={2} align="center">
            <IconButton
              size="xs"
              variant="ghost"
              aria-label="Copy message ID"
              onClick={() => void navigator.clipboard.writeText(message.id)}
            >
              <FaCopy />
            </IconButton>
            {!isSystemMessage && (
              <IconButton
                size="xs"
                variant="ghost"
                aria-label="Ask AI about this message"
                onClick={() => setNoteDialogOpen(true)}
                disabled={isNoteLoading}
              >
                {isNoteLoading ? <Spinner size="sm" /> : '@'}
              </IconButton>
            )}
            {showExpand && onExpandThread && (
              <Button
                size="xs"
                variant="ghost"
                onClick={() => onExpandThread(message.id)}
              >
                <FaExpand />
                Expand
              </Button>
            )}
          </HStack>
        </HStack>
        {isSystemMessage ? (
          <Box
            data-color-mode={colorMode}
            fontSize="sm"
            maxW="100%"
            overflowX="auto"
          >
            <MDEditor.Markdown source={message.value} />
          </Box>
        ) : (
          <Text fontSize="sm" whiteSpace="pre-wrap">
            {message.value}
          </Text>
        )}
      </Box>

      <DialogRoot
        open={noteDialogOpen}
        onOpenChange={(e) => {
          if (!e.open) handleCloseNoteDialog();
        }}
        size="md"
      >
        <CenteredDialogContent maxW="min(90vw, 28rem)">
          <DialogHeader>
            <DialogTitle>Ask AI (@note)</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack gap={3} align="stretch">
              <Text fontSize="sm" color="fg.muted">
                The model sees this message and earlier messages in the same
                thread. Leave the box empty to use a default summary request.
              </Text>
              <Textarea
                value={noteQuery}
                onChange={(e) => setNoteQuery(e.target.value)}
                placeholder="Your question (optional)…"
                rows={4}
                resize="vertical"
                disabled={isNoteLoading}
              />
              {document && (
                <HStack justify="space-between" align="center" gap={3}>
                  <Text fontSize="sm" color="fg.muted" flex={1}>
                    Apply edits to document (saved immediately)
                  </Text>
                  <Switch.Root
                    checked={applyDocumentEdits}
                    onCheckedChange={(e) => setApplyDocumentEdits(e.checked)}
                    disabled={isNoteLoading}
                  >
                    <Switch.HiddenInput />
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                  </Switch.Root>
                </HStack>
              )}
            </VStack>
          </DialogBody>
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button variant="outline" disabled={isNoteLoading}>
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              colorPalette="blue"
              onClick={() => void handleSubmitNote()}
              loading={isNoteLoading}
              disabled={isNoteLoading}
            >
              Run
            </Button>
          </DialogFooter>
        </CenteredDialogContent>
      </DialogRoot>

      {variant === 'collapsed' && childMessages && childMessages.length > 0 && (
        <VStack
          gap={2}
          align="stretch"
          mt={2}
          pl={6}
          borderLeftWidth="2px"
          borderLeftColor="var(--border-base-color)"
        >
          {childMessages.map((child) => (
            <CollapsedChildSummary
              key={child.id}
              message={child}
              onRunChatMessageQuery={onRunChatMessageQuery}
              chatMessageQueryLoadingId={chatMessageQueryLoadingId}
            />
          ))}
        </VStack>
      )}

      {variant === 'expanded' && childMessages && childMessages.length > 0 && (
        <VStack
          gap={2}
          align="stretch"
          mt={2}
          pl={6}
          borderLeftWidth="2px"
          borderLeftColor="var(--border-base-color)"
        >
          {childMessages.map((child) => (
            <MessageCard
              key={child.id}
              message={child}
              variant="expanded"
              onRunChatMessageQuery={onRunChatMessageQuery}
              chatMessageQueryLoadingId={chatMessageQueryLoadingId}
            />
          ))}
        </VStack>
      )}
    </Box>
  );
}

function CollapsedChildSummary({
  message,
  onRunChatMessageQuery,
  chatMessageQueryLoadingId,
}: {
  message: MessageFieldsFragment;
  onRunChatMessageQuery: (
    messageId: string,
    query: string,
    options?: ChatMessageQueryOptions,
  ) => Promise<void>;
  chatMessageQueryLoadingId: string | null;
}) {
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteQuery, setNoteQuery] = useState('');
  const [applyDocumentEdits, setApplyDocumentEdits] = useState(false);
  const isSystemMessage = message.userId === 'system';
  const isNoteLoading = chatMessageQueryLoadingId === message.id;
  const preview = summarizeMessageForThreadPreview(message.value);

  const documentContext = message.messageContexts?.find(
    (ctx) => ctx.name === 'documentId' && ctx.document,
  );
  const document = documentContext?.document;

  const handleCloseNoteDialog = () => {
    setNoteDialogOpen(false);
    setNoteQuery('');
    setApplyDocumentEdits(false);
  };

  const handleSubmitNote = async () => {
    try {
      await onRunChatMessageQuery(message.id, noteQuery, {
        applyDocumentEdits: document ? applyDocumentEdits : false,
      });
      handleCloseNoteDialog();
    } catch {
      /* Error toast shown by parent */
    }
  };

  return (
    <Box py={1}>
      <HStack justify="space-between" align="start" gap={2} mb={1}>
        <HStack gap={2} align="center" flexWrap="wrap" minW={0}>
          <Text
            fontSize="xs"
            fontWeight="medium"
            color={isSystemMessage ? 'var(--brand-base-color)' : 'fg.muted'}
          >
            {message.author?.displayName ?? 'Unknown'}
          </Text>
          <Text fontSize="xs" color="fg.muted">
            ·
          </Text>
          <Text fontSize="xs" color="fg.muted">
            {new Date(message.createdAt).toLocaleString()}
          </Text>
          {getSourceIcon(message.source) && (
            <Box
              as={getSourceIcon(message.source)!}
              aria-label={message.source}
              color={
                message.source === 'ai-note'
                  ? 'var(--brand-base-color)'
                  : 'fg.subtle'
              }
              fontSize="xs"
              display="inline-flex"
            />
          )}
        </HStack>
        <HStack gap={1} flexShrink={0}>
          <IconButton
            size="xs"
            variant="ghost"
            aria-label="Copy message ID"
            onClick={() => void navigator.clipboard.writeText(message.id)}
          >
            <FaCopy />
          </IconButton>
          {!isSystemMessage && (
            <IconButton
              size="xs"
              variant="ghost"
              aria-label="Ask AI about this message"
              onClick={() => setNoteDialogOpen(true)}
              disabled={isNoteLoading}
            >
              {isNoteLoading ? <Spinner size="sm" /> : '@'}
            </IconButton>
          )}
        </HStack>
      </HStack>
      <Text fontSize="xs" color="fg.muted" lineHeight="short">
        {preview}
      </Text>

      <DialogRoot
        open={noteDialogOpen}
        onOpenChange={(e) => {
          if (!e.open) handleCloseNoteDialog();
        }}
        size="md"
      >
        <CenteredDialogContent maxW="min(90vw, 28rem)">
          <DialogHeader>
            <DialogTitle>Ask AI (@note)</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack gap={3} align="stretch">
              <Text fontSize="sm" color="fg.muted">
                The model sees this message and earlier messages in the same
                thread. Leave the box empty to use a default summary request.
              </Text>
              <Textarea
                value={noteQuery}
                onChange={(e) => setNoteQuery(e.target.value)}
                placeholder="Your question (optional)…"
                rows={4}
                resize="vertical"
                disabled={isNoteLoading}
              />
              {document && (
                <HStack justify="space-between" align="center" gap={3}>
                  <Text fontSize="sm" color="fg.muted" flex={1}>
                    Apply edits to document (saved immediately)
                  </Text>
                  <Switch.Root
                    checked={applyDocumentEdits}
                    onCheckedChange={(e) => setApplyDocumentEdits(e.checked)}
                    disabled={isNoteLoading}
                  >
                    <Switch.HiddenInput />
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                  </Switch.Root>
                </HStack>
              )}
            </VStack>
          </DialogBody>
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button variant="outline" disabled={isNoteLoading}>
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              colorPalette="blue"
              onClick={() => void handleSubmitNote()}
              loading={isNoteLoading}
              disabled={isNoteLoading}
            >
              Run
            </Button>
          </DialogFooter>
        </CenteredDialogContent>
      </DialogRoot>
    </Box>
  );
}
